import type {NewSessionConfiguration} from '@github-ui/one-copilot-composer/new'
import type {AvailableOrganization, TaskComposerProps} from '@github-ui/one-copilot-composer/shared'

import type {CopilotAgentsPayload} from './copilot-agents-route'

/**
 * The configuration the Agents home composer runs on, independent of which
 * composer the rollout renders.
 *
 * Both destinations read it through the mappers below rather than each spelling
 * the payload out, so a value added for one of them cannot go missing from the
 * other while the migration is in flight.
 */
export interface AgentsComposerConfiguration {
  supportsManagedSandbox: boolean
  agentQuota: CopilotAgentsPayload['agentQuotaSnapshot']
  mcpPermissionGateEnabled: boolean | undefined
  currentUserLogin: string | undefined
  currentUserAvatarUrl: string | undefined
  availableOrganizations: AvailableOrganization[]
  lastContributedOrganizationLogin: string | undefined
  /** Clears the accepted draft while preserving it when submission fails. */
  clearOnSubmit: boolean
  disableWhileSubmitting: boolean
}

export function buildAgentsComposerConfiguration(payload: CopilotAgentsPayload): AgentsComposerConfiguration {
  return {
    supportsManagedSandbox: payload.enterpriseSandboxEnabled !== false,
    agentQuota: payload.agentQuotaSnapshot,
    mcpPermissionGateEnabled: payload.mcpPermissionGateEnabled,
    currentUserLogin: payload.currentUser?.login,
    currentUserAvatarUrl: payload.currentUser?.primaryAvatarUrl,
    availableOrganizations: payload.userOrganizations ?? [],
    lastContributedOrganizationLogin: payload.lastContributedOrganizationLogin,
    clearOnSubmit: true,
    disableWhileSubmitting: true,
  }
}

export function toNewComposerConfiguration(
  configuration: AgentsComposerConfiguration,
  scopePicker: NewSessionConfiguration['scopePicker'],
  session: Pick<
    NewSessionConfiguration,
    | 'mode'
    | 'onModeChange'
    | 'approvalMode'
    | 'availableApprovalModes'
    | 'onApprovalModeChange'
    | 'modePicker'
    | 'disableSandboxSubmission'
    | 'scopeOwnerPanel'
  > = {},
): NewSessionConfiguration {
  return {
    supportsManagedSandbox: configuration.supportsManagedSandbox,
    agentQuota: configuration.agentQuota,
    mcpPermissionGateEnabled: configuration.mcpPermissionGateEnabled,
    currentUserLogin: configuration.currentUserLogin,
    currentUserAvatarUrl: configuration.currentUserAvatarUrl,
    availableOrganizations: configuration.availableOrganizations,
    lastContributedOrganizationLogin: configuration.lastContributedOrganizationLogin,
    scopePicker,
    ...(session.scopeOwnerPanel ? {scopeOwnerPanel: session.scopeOwnerPanel} : {}),
    ...(session.disableSandboxSubmission ? {disableSandboxSubmission: true} : {}),
    ...(session.mode ? {mode: session.mode} : {}),
    ...(session.onModeChange ? {onModeChange: session.onModeChange} : {}),
    ...(session.approvalMode ? {approvalMode: session.approvalMode} : {}),
    ...(session.availableApprovalModes ? {availableApprovalModes: session.availableApprovalModes} : {}),
    ...(session.onApprovalModeChange ? {onApprovalModeChange: session.onApprovalModeChange} : {}),
    ...(session.modePicker ? {modePicker: session.modePicker} : {}),
  }
}

type LegacyComposerConfigurationProps = Pick<
  TaskComposerProps,
  | 'supportsManagedSandbox'
  | 'agentQuotaSnapshot'
  | 'mcpPermissionGateEnabled'
  | 'currentUserLogin'
  | 'currentUserAvatarUrl'
  | 'availableOrganizations'
  | 'lastContributedOrganizationLogin'
  | 'shouldClearTextOnSubmit'
  | 'disableWhileSubmitting'
>

export function toLegacyComposerProps(configuration: AgentsComposerConfiguration): LegacyComposerConfigurationProps {
  return {
    supportsManagedSandbox: configuration.supportsManagedSandbox,
    agentQuotaSnapshot: configuration.agentQuota,
    mcpPermissionGateEnabled: configuration.mcpPermissionGateEnabled,
    currentUserLogin: configuration.currentUserLogin,
    currentUserAvatarUrl: configuration.currentUserAvatarUrl,
    availableOrganizations: configuration.availableOrganizations,
    lastContributedOrganizationLogin: configuration.lastContributedOrganizationLogin,
    shouldClearTextOnSubmit: configuration.clearOnSubmit,
    disableWhileSubmitting: configuration.disableWhileSubmitting,
  }
}
